import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Loader2, Slack } from 'lucide-react';
import { Button, Card } from '@/components/ui-v2';
import { cn } from '@/lib/utils';
import { getApiErrorMessage, getRequestContext } from '@/lib/http';
import {
    disconnectSlack,
    getSlackConnections,
    getSlackInstallUrl,
    type SlackWorkspaceConnection,
} from './slack.api';

type Banner = { type: 'success' | 'error'; message: string } | null;

function formatDate(iso: string): string {
    return new Date(iso).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
}

/**
 * Self-serve Slack connection card. A Lantern workspace can connect to several
 * Slack workspaces (e.g. an agency serving multiple clients), so this lists
 * every connection with its own Disconnect and a "Connect another" action.
 */
export default function SlackIntegrationCard() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [connecting, setConnecting] = useState(false);
    const [disconnectingTeam, setDisconnectingTeam] = useState<string | null>(null);
    const [connections, setConnections] = useState<SlackWorkspaceConnection[]>([]);
    const [banner, setBanner] = useState<Banner>(null);

    const workspaceId = getRequestContext().workspaceId || undefined;

    const loadConnections = useCallback(async () => {
        setLoading(true);
        try {
            setConnections(await getSlackConnections(workspaceId));
        } catch {
            // Non-fatal — treat as none so the Connect action stays available.
            setConnections([]);
        } finally {
            setLoading(false);
        }
    }, [workspaceId]);

    useEffect(() => {
        loadConnections();
    }, [loadConnections]);

    // Surface the OAuth callback result once, then strip the params so a refresh
    // doesn't replay the banner.
    useEffect(() => {
        const flag = searchParams.get('slackConnected');
        if (!flag) return;

        if (flag === '1') {
            setBanner({ type: 'success', message: 'Slack connected successfully.' });
            loadConnections();
        } else {
            const code = searchParams.get('error');
            setBanner({
                type: 'error',
                message: code ? `Slack connection failed (${code}).` : 'Slack connection failed.',
            });
        }

        const next = new URLSearchParams(searchParams);
        next.delete('slackConnected');
        next.delete('error');
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams, loadConnections]);

    const handleConnect = async () => {
        setConnecting(true);
        setBanner(null);
        try {
            const authorizeUrl = await getSlackInstallUrl(workspaceId);
            window.location.assign(authorizeUrl);
        } catch (err) {
            setBanner({ type: 'error', message: getApiErrorMessage(err, 'Could not start Slack connection.') });
            setConnecting(false);
        }
    };

    const handleDisconnect = async (conn: SlackWorkspaceConnection) => {
        setDisconnectingTeam(conn.teamId);
        setBanner(null);
        try {
            await disconnectSlack(conn.teamId, workspaceId);
            setBanner({ type: 'success', message: `Disconnected ${conn.teamName || 'workspace'}.` });
            await loadConnections();
        } catch (err) {
            setBanner({ type: 'error', message: getApiErrorMessage(err, 'Could not disconnect Slack.') });
        } finally {
            setDisconnectingTeam(null);
        }
    };

    const hasConnections = connections.length > 0;

    return (
        <Card padding="lg" className="mt-4">
            {banner && (
                <div
                    role="alert"
                    className={cn(
                        'mb-4 flex items-center gap-2 rounded-lg px-3 py-2 text-[13px]',
                        banner.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700',
                    )}
                >
                    {banner.type === 'success' ? <CheckCircle2 size={15} /> : <AlertCircle size={15} />}
                    <span className="min-w-0 flex-1">{banner.message}</span>
                    <button
                        type="button"
                        onClick={() => setBanner(null)}
                        aria-label="Dismiss"
                        className="text-lg leading-none opacity-70 hover:opacity-100"
                    >
                        &times;
                    </button>
                </div>
            )}

            <div className="flex items-center gap-4">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-700">
                    <Slack size={20} />
                </div>

                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="text-sm font-semibold text-gray-900">Slack</span>
                    <span className="text-[13px] text-gray-500">
                        Post updates and collaborate with agents directly in your Slack workspace.
                    </span>
                </div>

                <div className="flex flex-shrink-0 items-center gap-3">
                    <Button
                        variant={hasConnections ? 'secondary' : 'primary'}
                        size={hasConnections ? 'sm' : 'md'}
                        onClick={handleConnect}
                        disabled={loading || connecting}
                    >
                        {connecting ? 'Opening Slack…' : hasConnections ? 'Connect another' : 'Connect Slack'}
                    </Button>
                </div>
            </div>

            {loading ? (
                <div className="mt-4 flex items-center gap-1.5 text-[13px] text-gray-500">
                    <Loader2 size={13} className="animate-spin" />
                    Checking connections&hellip;
                </div>
            ) : hasConnections ? (
                <ul className="mt-4 divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-100">
                    {connections.map((conn) => (
                        <li key={conn.teamId} className="flex items-center gap-3 px-3 py-2.5">
                            <CheckCircle2 size={15} className="flex-shrink-0 text-green-600" />
                            <div className="flex min-w-0 flex-1 flex-col">
                                <span className="truncate text-[13px] font-medium text-gray-800">
                                    {conn.teamName || 'Slack workspace'}
                                </span>
                                {conn.connectedAt && (
                                    <span className="text-[12px] text-gray-400">
                                        Connected {formatDate(conn.connectedAt)}
                                    </span>
                                )}
                            </div>
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => handleDisconnect(conn)}
                                disabled={disconnectingTeam === conn.teamId}
                            >
                                {disconnectingTeam === conn.teamId ? 'Disconnecting…' : 'Disconnect'}
                            </Button>
                        </li>
                    ))}
                </ul>
            ) : null}
        </Card>
    );
}
